<script>
import { mapActions, mapMutations } from 'vuex';
import { cloneDeep } from 'lodash';
import RbxRemoteCombobox from '@/shared/reybex/RbxRemoteCombobox';
import RbxRemoteComboboxMultiple from '@/shared/reybex/RbxRemoteComboboxMultiple';
import validatorMixin from '@/mixins/validator';
import snackbarMixin from '@/mixins/snackbar';
import RbxDatePicker from '../../../shared/reybex/RbxDatePicker';
import Documents from './Documents';

export default {
  name: 'TaskDialog',
  components: {
    RbxRemoteCombobox,
    RbxRemoteComboboxMultiple,
    RbxDatePicker,
    Documents,
  },
  mixins: [snackbarMixin, validatorMixin],
  props: {
    task: {
      type: Object,
      required: false,
      default: () => {},
    },
    measureItem: {
      type: Object,
      required: false,
      default: () => {},

    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      visible: false,
      isLoading: false,
      rules: {
        name: { required: true },
      },
      baseFormData: {
        status: 0,
      },
      formData: {},
      measureData: {},
      priorityOptions: [{ id: 3, text: 'Sehr hoch' }, { id: 2, text: 'Hoch' }, { id: 1, text: 'Normal' }, { id: 0, text: 'Niedrig' }],
      statusOptions: [{ id: 0, text: 'Offen' }, { id: 1, text: 'In Bearbeitung' }, { id: 2, text: 'Geschlossen' }],
      isStatusUpdated: false,
      localDisabled: false,
    };
  },
  created() {
    this.formData = Object.assign({}, this.baseFormData, this.task);
    this.measureData = Object.assign({}, {}, this.measureItem);
    this.localDisabled = this.disabled;

    if (!this.task.id && this.measureData && this.measureData.id) {
      this.formData.name = this.measureData.name;
      this.formData.description = this.measureData.description;
      this.formData.priority = this.measureData.priority;
    }
    this.formData.measure = { id: this.measureData.id };
  },
  mounted() {
    this.visible = true;
  },
  beforeDestroy() {
    this.visible = false;
  },
  methods: {
    ...mapMutations('taskList', ['updateTask']),
    ...mapActions('taskList', ['getAll']),
    ...mapActions('privacyAuditDetails', ['saveTask']),
    ...mapActions('privacyAuditDetails', { fetchData: 'fetchMeasureList' }),
    submit(type) {
      if (!this.validateForm()) return false;

      this.isLoading = true;

      return this.saveTask(this.formData)
        .then(({ data }) => {
          this.showSuccessMessage('Der Task wurde aktualisiert.');
          this.updateTask(this.formData);

          if (type === 'new') {
            this.formData = cloneDeep(this.baseFormData);
          } else {
            this.close();

            if (type === 'detail') {
              this.$router.push(`/task/${data.id}`);
            }
          }

          if (this.isStatusUpdated) {
            this.getAll();
          }

          this.$emit('update');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    close() {
      this.visible = false;
      this.$emit('cancel');
    },
    onDeadlineInput(newDate) {
      this.formData.deadline = newDate;
    },
    onResubmissionInput(newDate) {
      this.formData.resubmission = newDate;
    },
  },
};
</script>

<template>
  <v-dialog v-model="visible"
            persistent
            max-width="1000px">

    <v-card>
      <v-card-title class="title pa-3">
        Aufgabe bearbeiten
        <v-spacer/>
        <v-btn @click="close" fab small flat>
          <v-icon>clear</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md class="pa-0">
          <v-layout row wrap>

            <v-flex md12>
              <v-layout row wrap>
                <v-flex md6>
                  <v-text-field
                    v-model="formData.name"
                    label="Bezeichnung"
                    class="caption"
                    height="22"
                  />
                </v-flex>
                <v-flex md6>
                  <rbx-remote-combobox
                    v-model="formData.user"
                    :selected="formData.user"
                    searchable
                    domain="User"
                    label="Benutzer" />
                </v-flex>
              </v-layout>
            </v-flex>

            <v-flex md12>
              <v-layout row wrap>
                <v-flex md6>
                  <v-select
                    :items="priorityOptions"
                    v-model="formData.priority"
                    item-value="id"
                    item-text="text"
                    label="Priorität" />
                </v-flex>
                <v-flex md6>
                  <v-select
                    :items="statusOptions"
                    v-model="formData.status"
                    @change="isStatusUpdated = true"
                    item-value="id"
                    item-text="text"
                    label="Status" />
                </v-flex>
              </v-layout>
            </v-flex>

            <v-flex md12>
              <v-textarea
                v-model="formData.description"
                label="Beschreibung"
                class="caption"
                height="66" />
            </v-flex>

            <v-flex md6>
              <rbx-date-picker label="Ende" :value="formData.deadline" @input="onDeadlineInput" />
            </v-flex>
            <v-flex md6>
              <rbx-date-picker
                label="Wiedervorlage"
                :value="formData.resubmission"
                @input="onResubmissionInput"
              />
            </v-flex>

            <v-flex md12>
              <Documents
                :resource="'task'"
                :id="task.id"
                :allowed-file-types="['csv', 'doc', 'pdf', 'rtf', 'tex', 'txt', 'xlsx']"
              />
            </v-flex>

          </v-layout>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn @click.native="close"
               color="red"
               flat>
          Schließen
        </v-btn>
        <v-btn @click.native="submit"
               color="blue"
               :loading="isLoading"
               flat>
          Speichern
        </v-btn>
      </v-card-actions>
    </v-card>

  </v-dialog>
</template>
